import { useEffect, useRef, useState, useCallback } from "react"
import { motion, AnimatePresence, useMotionValue, useTransform, useAnimation } from "framer-motion"
import {
  StarIcon,
  CheckBadgeIcon,
  MapPinIcon,
  XMarkIcon,
  HeartIcon,
} from "@heroicons/react/24/solid"
import { AcademicCapIcon, BriefcaseIcon } from "@heroicons/react/24/outline"

const CARDS = [
  {
    name: "Анна Петрова",
    role: "Менеджер WB",
    city: "Москва",
    rating: 4.9,
    reviews: 38,
    experience: "3 года",
    courses: 7,
    level: "Expert",
    tags: ["SEO карточек", "Аналитика", "Реклама WB"],
    badge: true,
    gradient: "from-violet-500 via-purple-500 to-indigo-600",
    glow: "shadow-violet-200",
    initials: "АП",
  },
  {
    name: "Дмитрий Ким",
    role: "Поставщик · Ozon",
    city: "Алматы",
    rating: 4.7,
    reviews: 24,
    experience: "5 лет",
    courses: 4,
    level: "Middle",
    tags: ["Поставки из Китая", "Переговоры", "Таможня"],
    badge: false,
    gradient: "from-blue-500 via-cyan-500 to-sky-500",
    glow: "shadow-blue-200",
    initials: "ДК",
  },
  {
    name: "Мария Соколова",
    role: "SMM · Маркетинг",
    city: "Санкт-Петербург",
    rating: 5.0,
    reviews: 61,
    experience: "4 года",
    courses: 9,
    level: "Expert",
    tags: ["Контент", "Инфлюенсеры", "ROMI"],
    badge: true,
    gradient: "from-pink-500 via-rose-500 to-red-400",
    glow: "shadow-pink-200",
    initials: "МС",
  },
  {
    name: "Алексей Воронов",
    role: "Логист · WB",
    city: "Екатеринбург",
    rating: 4.6,
    reviews: 19,
    experience: "2 года",
    courses: 3,
    level: "Middle",
    tags: ["FBO / FBS", "Склады", "Оптимизация"],
    badge: false,
    gradient: "from-emerald-500 via-teal-500 to-green-500",
    glow: "shadow-emerald-200",
    initials: "АВ",
  },
  {
    name: "Нурлан Асанов",
    role: "Продавец · WB / Ozon",
    city: "Бишкек",
    rating: 4.8,
    reviews: 31,
    experience: "3 года",
    courses: 5,
    level: "Middle",
    tags: ["Текстиль", "Товары из КР", "Аналитика"],
    badge: true,
    gradient: "from-amber-500 via-orange-500 to-yellow-500",
    glow: "shadow-amber-200",
    initials: "НА",
  },
]

const LEVEL: Record<string, { label: string; cls: string }> = {
  Expert: { label: "Expert", cls: "bg-violet-100 text-violet-700 border-violet-200" },
  Middle: { label: "Middle", cls: "bg-blue-100 text-blue-700 border-blue-200" },
}

const SWIPE_THRESHOLD = 80

/* ── draggable card ─────────────────────────────────────── */

const DraggableCard = ({
  card,
  onSwipe,
  onDragging,
  isFirst,
}: {
  card: typeof CARDS[0]
  onSwipe: (dir: 1 | -1) => void
  onDragging: (x: number) => void
  isFirst: boolean
}) => {
  const x = useMotionValue(0)
  const controls = useAnimation()

  const rotate = useTransform(x, [-200, 200], [-18, 18])
  const likeOpacity = useTransform(x, [20, 80], [0, 1])
  const nopeOpacity = useTransform(x, [-80, -20], [1, 0])

  // hint wiggle on first card load
  useEffect(() => {
    if (!isFirst) return
    const timeout = setTimeout(async () => {
      await controls.start({ x: -28, rotate: -4, transition: { duration: 0.35, ease: "easeInOut" } })
      await controls.start({ x: 28, rotate: 4, transition: { duration: 0.4, ease: "easeInOut" } })
      await controls.start({ x: 0, rotate: 0, transition: { type: "spring", stiffness: 280, damping: 18 } })
    }, 900)
    return () => clearTimeout(timeout)
  }, [isFirst])

  const handleDragEnd = async (_: any, info: { offset: { x: number } }) => {
    onDragging(0)
    const ox = info.offset.x
    if (Math.abs(ox) > SWIPE_THRESHOLD) {
      const dir = ox > 0 ? 1 : -1
      await controls.start({ x: dir * 500, rotate: dir * 25, opacity: 0, transition: { duration: 0.35 } })
      onSwipe(dir)
    } else {
      controls.start({ x: 0, rotate: 0, transition: { type: "spring", stiffness: 300, damping: 20 } })
    }
  }

  return (
    <motion.div
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.8}
      animate={controls}
      style={{ x, rotate, touchAction: "none", width: 320, height: 430, top: 0 } as any}
      onDrag={(_, info) => onDragging(info.offset.x)}
      onDragEnd={handleDragEnd}
      className={`absolute z-10 rounded-3xl bg-white overflow-hidden shadow-2xl ${card.glow} cursor-grab active:cursor-grabbing select-none`}
    >
      {/* LIKE stamp */}
      <motion.div
        style={{ opacity: likeOpacity }}
        className="absolute top-8 left-6 z-20 rotate-[-20deg] border-4 border-emerald-500 rounded-xl px-3 py-1"
      >
        <span className="text-emerald-500 font-black text-2xl tracking-widest">LIKE</span>
      </motion.div>

      {/* NOPE stamp */}
      <motion.div
        style={{ opacity: nopeOpacity }}
        className="absolute top-8 right-6 z-20 rotate-[20deg] border-4 border-red-500 rounded-xl px-3 py-1"
      >
        <span className="text-red-500 font-black text-2xl tracking-widest">NOPE</span>
      </motion.div>

      {/* Gradient header */}
      <div className={`relative h-52 bg-gradient-to-br ${card.gradient} flex flex-col items-center justify-end pb-5 overflow-hidden`}>
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="absolute top-4 left-4 flex items-center gap-1 bg-black/20 backdrop-blur-sm rounded-full px-2.5 py-1">
          <StarIcon className="w-3 h-3 text-yellow-300" />
          <span className="text-white text-xs font-bold">{card.rating}</span>
          <span className="text-white/60 text-[10px]">({card.reviews})</span>
        </div>
        <div className={`absolute top-4 right-4 text-[10px] font-bold px-2.5 py-1 rounded-full border ${LEVEL[card.level].cls}`}>
          {LEVEL[card.level].label}
        </div>
        <div className="w-20 h-20 rounded-2xl bg-white/25 backdrop-blur-sm flex items-center justify-center text-white font-black text-2xl shadow-xl ring-4 ring-white/30 mb-3">
          {card.initials}
        </div>
        <p className="text-white font-bold text-lg leading-tight drop-shadow">{card.name}</p>
        <p className="text-white/80 text-xs mt-0.5 font-medium">{card.role}</p>
      </div>

      {/* Body */}
      <div className="px-5 py-4 space-y-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <MapPinIcon className="w-3.5 h-3.5" />
          {card.city}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {card.tags.map((tag) => (
            <span key={tag} className="bg-gray-50 border border-gray-200 text-gray-600 text-[11px] font-medium px-2.5 py-1 rounded-full">
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-3 pt-1 border-t border-gray-100">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <AcademicCapIcon className="w-4 h-4 text-blue-400" />
            <span><b className="text-gray-700">{card.courses}</b> курсов</span>
          </div>
          <div className="w-px h-3.5 bg-gray-200" />
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <BriefcaseIcon className="w-4 h-4 text-violet-400" />
            <span>Опыт <b className="text-gray-700">{card.experience}</b></span>
          </div>
          {card.badge && (
            <>
              <div className="w-px h-3.5 bg-gray-200" />
              <div className="flex items-center gap-1 text-xs text-emerald-600 font-semibold">
                <CheckBadgeIcon className="w-4 h-4" />
                GS
              </div>
            </>
          )}
        </div>
      </div>
    </motion.div>
  )
}

/* ── main ───────────────────────────────────────────────── */

export const SwipeCards = () => {
  const [index, setIndex] = useState(0)
  const [showConnect, setShowConnect] = useState(false)
  const [dragX, setDragX] = useState(0)
  const autoRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isDragging = useRef(false)

  const card = CARDS[index % CARDS.length]

  const startAuto = () => {
    if (autoRef.current) clearInterval(autoRef.current)
    autoRef.current = setInterval(() => {
      if (!isDragging.current) handleSwipe(1)
    }, 3200)
  }

  useEffect(() => {
    startAuto()
    return () => { if (autoRef.current) clearInterval(autoRef.current) }
  }, [index])

  const handleSwipe = (dir: 1 | -1) => {
    if (dir === 1) {
      setShowConnect(true)
      setTimeout(() => setShowConnect(false), 900)
    }
    setIndex((i) => (i + 1) % CARDS.length)
    startAuto()
  }

  const handleDragging = (x: number) => {
    isDragging.current = Math.abs(x) > 5
    setDragX(x)
  }

  // button glow based on drag direction
  const likeActive = dragX > 40
  const nopeActive = dragX < -40

  return (
    <div className="relative flex flex-col items-center" style={{ width: 340, height: 510 }}>

      {/* Back cards */}
      <div
        className="absolute rounded-3xl bg-white border border-gray-100 shadow-md"
        style={{ width: 300, height: 420, top: 30, transform: "rotate(-5deg) scale(0.92)", zIndex: 1, opacity: 0.45 }}
      />
      <div
        className="absolute rounded-3xl bg-white border border-gray-100 shadow-md"
        style={{ width: 320, height: 428, top: 14, transform: "rotate(2.5deg) scale(0.96)", zIndex: 2, opacity: 0.7 }}
      />

      {/* Draggable card */}
      <DraggableCard
        key={index}
        card={card}
        onSwipe={handleSwipe}
        onDragging={handleDragging}
        isFirst={index === 0}
      />

      {/* Connect overlay (auto-swipe) */}
      <AnimatePresence>
        {showConnect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute z-30 rounded-3xl inset-x-[10px] top-0 flex items-center justify-center backdrop-blur-sm bg-white/70"
            style={{ height: 430 }}
          >
            <motion.div
              initial={{ scale: 0.5, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 1.1, opacity: 0 }}
              className="text-center"
            >
              <div className="text-5xl mb-2">🎉</div>
              <div className="text-3xl font-black text-emerald-500 tracking-tight">Connect!</div>
              <div className="text-sm text-gray-400 mt-1 font-medium">Взаимный интерес</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hint arrows */}
      <div className="absolute flex items-center gap-2" style={{ top: 442 }}>
        <motion.span
          animate={{ x: [-4, 0, -4] }}
          transition={{ repeat: Infinity, duration: 1.4, ease: "easeInOut" }}
          className="text-gray-300 text-lg"
        >
          ←
        </motion.span>
        <span className="text-[11px] text-gray-400 font-medium">свайп</span>
        <motion.span
          animate={{ x: [4, 0, 4] }}
          transition={{ repeat: Infinity, duration: 1.4, ease: "easeInOut" }}
          className="text-gray-300 text-lg"
        >
          →
        </motion.span>
      </div>

      {/* Action buttons */}
      <div className="absolute flex items-center gap-5" style={{ bottom: 0 }}>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => handleSwipe(-1)}
          className={`flex items-center justify-center rounded-full border-2 shadow-lg transition-all ${
            nopeActive
              ? "bg-red-500 border-red-500 shadow-red-200"
              : "bg-white border-gray-200 hover:bg-red-50 hover:border-red-300"
          }`}
          style={{ width: 54, height: 54 }}
        >
          <XMarkIcon className={`w-6 h-6 transition-colors ${nopeActive ? "text-white" : "text-gray-400"}`} />
        </motion.button>

        <div className="flex gap-1.5">
          {CARDS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index % CARDS.length ? "w-5 bg-blue-500" : "w-1.5 bg-gray-300"
              }`}
            />
          ))}
        </div>

        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => handleSwipe(1)}
          className={`flex items-center justify-center rounded-full border-2 shadow-lg transition-all ${
            likeActive
              ? "bg-emerald-500 border-emerald-500 shadow-emerald-200"
              : "bg-white border-gray-200 hover:bg-emerald-50 hover:border-emerald-300"
          }`}
          style={{ width: 54, height: 54 }}
        >
          <HeartIcon className={`w-6 h-6 transition-colors ${likeActive ? "text-white" : "text-gray-400"}`} />
        </motion.button>
      </div>
    </div>
  )
}
